import { ProForm, ProFormSelect } from '@ant-design/pro-components'
import type React from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'
import { useMergedFieldProps } from '../utils/mergeFieldProps'
import type { SelectOption, SelectOptions } from '../utils/selectField.utils'
import { findOptionByValue, normalizeOptions, unwrapSelectValue } from '../utils/selectField.utils'

interface RenderPrams<T = unknown> {
  value: T
  placeholder?: string
  mode: 'read' | 'edit'
  option?: SelectOption
  options?: SelectOptions
}

export type SelectFieldProps = React.ComponentProps<typeof ProFormSelect> & {
  render?: (params: RenderPrams) => React.ReactNode
  // labelRender?: SelectProps['labelRender']
  // optionRender?: SelectProps['optionRender']
  emptyState?: React.ReactNode
  /** Separator between values in read mode when multiple are selected. Defaults to ', ' */
  separator?: string
}

export function SelectField<TValue = unknown>(props: SelectFieldProps) {
  const fieldCtx = useFieldContext<TValue>()
  // Strip `readonly` from rest so it doesn't leak onto the underlying <select>
  // — React expects camelCase `readOnly`, lowercase triggers a console.error.
  const {
    formItemProps,
    fieldProps,
    proFieldProps,
    className,
    render,
    emptyState,
    separator = ', ',
    readonly: _readonly,
    ...rest
  } = props

  const editable = useEditableFieldMode<TValue>()

  const mergeFieldCtx = {
    ...fieldCtx,
    name: fieldCtx.name,
    state: fieldCtx.state,
    handleChange: (next: unknown) => fieldCtx.handleChange(next as TValue),
  }

  const merged = useMergedFieldProps({
    fieldCtx: mergeFieldCtx,
    editable,
    props,
  })

  const isMultiple =
    rest.mode === 'multiple' ||
    merged.fieldProps.mode === 'multiple' ||
    rest.mode === 'tags' ||
    merged.fieldProps.mode === 'tags'

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      {editable.mode === 'read' &&
      (!merged.fieldProps.value || (merged.fieldProps.value as string[]).length === 0) &&
      emptyState ? (
        <ProForm.Item
          {...rest}
          label={
            rest.label && (
              <EditableLabel
                mode={editable.mode}
                enterEdit={editable.enterEdit}
                exitRead={editable.exitRead}
                onUndo={editable.undoAndExit}
                canUndo={editable.canUndo}
                showActions={!props.readonly}
              >
                {rest.label}
              </EditableLabel>
            )
          }
        >
          {emptyState}
        </ProForm.Item>
      ) : (
        <ProFormSelect
          {...rest}
          label={
            rest.label && (
              <EditableLabel
                mode={editable.mode}
                enterEdit={editable.enterEdit}
                exitRead={editable.exitRead}
                onUndo={editable.undoAndExit}
                canUndo={editable.canUndo}
                showActions={!props.readonly}
              >
                {rest.label}
              </EditableLabel>
            )
          }
          proFieldProps={{
            ...merged.proFieldProps,
            render: (_raw: unknown, params: Omit<RenderPrams, 'option'>) => {
              const allOptions = normalizeOptions(params.options ?? rest.options ?? merged.fieldProps.options)
              const fieldValue = merged.fieldProps.value
              const effectiveValue =
                isMultiple && Array.isArray(merged.fieldProps.value) && !Array.isArray(params.value)
                  ? merged.fieldProps.value
                  : (params.value ?? fieldValue)

              if (Array.isArray(effectiveValue)) {
                return effectiveValue
                  .map(v => {
                    const lookupValue = unwrapSelectValue(v)
                    const opt = findOptionByValue(allOptions, lookupValue)
                    if (render) return render({ ...params, value: v, option: opt, options: allOptions })
                    return (opt?.label ?? opt?.value ?? lookupValue) as React.ReactNode
                  })
                  .reduce<React.ReactNode[]>((acc, node, i) => {
                    if (i > 0) acc.push(separator)
                    acc.push(node)
                    return acc
                  }, [])
              }
              const lookupValue = unwrapSelectValue(effectiveValue)
              const option = findOptionByValue(allOptions, lookupValue)
              if (render) {
                return render({ ...params, value: effectiveValue, option, options: allOptions })
              }
              if (option?.label) {
                return option.label
              }
              if (option?.value) {
                return option.value
              }
              return lookupValue ?? '-'
            },
          }}
          formItemProps={merged.formItemProps}
          fieldProps={merged.fieldProps}
        />
      )}
    </EditableShell>
  )
}
